import toMillis from './toMillis'

/**
 * @typedef {object} ResolvedStartDate
 * @property {number} ms - Het startmoment in milliseconden
 * @property {'startDate'|'statusHistory'|'created'} source - Waar de datum vandaan komt
 * @property {boolean} estimated - `true` zodra de datum een benadering is
 */

/**
 * Het startmoment van een deelnemer of huishouden, uit de best beschikbare bron.
 *
 * De volgorde is niet willekeurig. Op de live gegevens (2026-08-27) heeft maar
 * 151 van de 475 actieve deelnemers een ingevulde `StartDate`; `StatusHistory`
 * is net in gebruik genomen en vult zich pas met de tijd; de aanmaakdatum is er
 * altijd. Samen dekken ze iedereen.
 *
 * De laatste twee bronnen zijn een schátting — de aanmaakdatum van een profiel
 * is niet de instroomdatum, zeker niet bij geïmporteerde gegevens. Daarom komt
 * `estimated` mee naar buiten: een geschatte datum die eruitziet als een harde
 * datum is erger dan geen datum, dus het scherm moet dat kunnen laten zien.
 *
 * Huishoudens (`Adresses`) hebben geen `StartDate`-veld; die vallen vanzelf
 * door naar de statushistorie of de aanmaakdatum.
 *
 * @param {object} args
 * @param {{ID?: string, StartDate?: *, Timestamp?: *}|null} [args.entity] - De deelnemer of het huishouden
 * @param {Array<{EntityID?: string, ChangedAt?: *, Deleted?: boolean}>} [args.statusEntries]
 *        Statushistorie; rijen van andere entiteiten mogen erin zitten
 * @returns {ResolvedStartDate|null} `null` als geen enkele bron een datum geeft
 */
const resolveStartDate = ({ entity, statusEntries } = {}) => {
    if (!entity) return null

    const explicit = toMillis(entity.StartDate)
    if (explicit !== null) return { ms: explicit, source: 'startDate', estimated: false }

    const own = (statusEntries || [])
        .filter((entry) => entry && !entry.Deleted && entry.EntityID === entity.ID)
        .map((entry) => toMillis(entry.ChangedAt))
        .filter((ms) => ms !== null)

    if (own.length > 0) {
        return { ms: Math.min(...own), source: 'statusHistory', estimated: true }
    }

    const created = toMillis(entity.Timestamp)
    if (created !== null) return { ms: created, source: 'created', estimated: true }

    return null
}

export default resolveStartDate
